const desktopMaxVisibleCards = 3;
const phoneMaxVisibleCards = 1;

const positionZones = [
  { top: [8, 24], left: [8, 32] },
  { top: [8, 26], left: [60, 78] },
  { top: [55, 74], left: [10, 34] },
  { top: [56, 76], left: [58, 78] },
];

const randomBetween = ([min, max]) => Math.round(min + Math.random() * (max - min));

const randomDelay = () => randomBetween([1400, 6200]);

const shortDelay = () => randomBetween([350, 900]);

const isPhoneView = () => window.matchMedia("(max-width: 37.5em)").matches;

const maxVisibleCards = () => (isPhoneView() ? phoneMaxVisibleCards : desktopMaxVisibleCards);

const shuffle = (items) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
};

const isCardDisplayable = (card) => window.getComputedStyle(card).display !== "none";

const randomAvailableZone = (activeZones) => {
  const availableZones = positionZones
    .map((_, index) => index)
    .filter((zoneIndex) => !activeZones.has(zoneIndex));

  return availableZones[Math.floor(Math.random() * availableZones.length)];
};

const getContentBoundary = (hero) => {
  const title = hero.querySelector(".hero-title");
  const cta = hero.querySelector(".hero-cta");
  const heroRect = hero.getBoundingClientRect();
  const rects = [title, cta].filter(Boolean).map((element) => element.getBoundingClientRect());
  const padding = 48;
  const mobilePadding = 28;
  const boundaryPadding = isPhoneView() ? mobilePadding : padding;

  return {
    top: Math.min(...rects.map((rect) => rect.top)) - heroRect.top - boundaryPadding,
    right: Math.max(...rects.map((rect) => rect.right)) - heroRect.left + boundaryPadding,
    bottom: Math.max(...rects.map((rect) => rect.bottom)) - heroRect.top + boundaryPadding,
    left: Math.min(...rects.map((rect) => rect.left)) - heroRect.left - boundaryPadding,
  };
};

const hasOverlap = (firstRect, secondRect) =>
  firstRect.left < secondRect.right &&
  firstRect.right > secondRect.left &&
  firstRect.top < secondRect.bottom &&
  firstRect.bottom > secondRect.top;

const getTravelRects = ({ centerX, top, width, height, startX, startY, endX, endY }) => [
  {
    top: top + startY,
    right: centerX - width / 2 + startX + width,
    bottom: top + startY + height,
    left: centerX - width / 2 + startX,
  },
  {
    top: top + endY,
    right: centerX - width / 2 + endX + width,
    bottom: top + endY + height,
    left: centerX - width / 2 + endX,
  },
];

const getSafePlacement = (hero, card, zoneIndex) => {
  const heroRect = hero.getBoundingClientRect();
  const contentBoundary = getContentBoundary(hero);
  const zone = positionZones[zoneIndex];
  const cardRect = card.getBoundingClientRect();
  const cardWidth = cardRect.width || (isPhoneView() ? 150 : 360);
  const cardHeight = cardRect.height || (isPhoneView() ? 110 : 260);
  const contentCenterX = (contentBoundary.left + contentBoundary.right) / 2;
  const contentCenterY = (contentBoundary.top + contentBoundary.bottom) / 2;

  for (let attempt = 0; attempt < 24; attempt += 1) {
    const centerX = (randomBetween(zone.left) / 100) * heroRect.width;
    const top = (randomBetween(zone.top) / 100) * heroRect.height;
    const driftDirectionX = centerX < contentCenterX ? -1 : 1;
    const driftDirectionY = top + cardHeight / 2 < contentCenterY ? -1 : 1;
    const driftX = driftDirectionX * randomBetween(isPhoneView() ? [36, 64] : [72, 120]);
    const driftY = driftDirectionY * randomBetween(isPhoneView() ? [28, 52] : [48, 88]);
    const startX = Math.round(driftX * -0.35);
    const startY = Math.round(driftY * -0.35);
    const endX = driftX;
    const endY = driftY;
    const travelRects = getTravelRects({ centerX, top, width: cardWidth, height: cardHeight, startX, startY, endX, endY });

    if (!travelRects.some((rect) => hasOverlap(rect, contentBoundary))) {
      return { centerX, top, startX, startY, endX, endY };
    }
  }

  const fallbackCenterX = zone.left[1] < 50 ? cardWidth : heroRect.width - cardWidth;
  const fallbackTop = zone.top[1] < 50 ? 32 : heroRect.height - cardHeight - 32;

  return {
    centerX: fallbackCenterX,
    top: fallbackTop,
    startX: zone.left[1] < 50 ? 32 : -32,
    startY: zone.top[1] < 50 ? 24 : -24,
    endX: zone.left[1] < 50 ? -96 : 96,
    endY: zone.top[1] < 50 ? -72 : 72,
  };
};

const prepareCard = (card, zoneIndex) => {
  const hero = document.querySelector("#hero");
  const placement = getSafePlacement(hero, card, zoneIndex);
  const rotation = randomBetween([-8, 8]);
  const duration = randomBetween([13500, 24500]);

  card.style.setProperty("--hero-card-top", `${placement.top}px`);
  card.style.setProperty("--hero-card-left", `${placement.centerX}px`);
  card.style.setProperty("--hero-card-rotation", `${rotation}deg`);
  card.style.setProperty("--hero-card-start-x", `${placement.startX}px`);
  card.style.setProperty("--hero-card-start-y", `${placement.startY}px`);
  card.style.setProperty("--hero-card-end-x", `${placement.endX}px`);
  card.style.setProperty("--hero-card-end-y", `${placement.endY}px`);
  card.style.setProperty("--hero-card-duration", `${duration}ms`);
};

export default function initHeroReviewFloat() {
  const cards = [...document.querySelectorAll(".hero-bg-card")];
  const activeCards = new Set();
  const activeZones = new Set();
  let reviewDeck = [];
  let pendingSpawn = null;

  if (!cards.length) return;

  const drawCard = () => {
    if (!reviewDeck.length) {
      reviewDeck = shuffle(cards.filter((card) => !activeCards.has(card) && isCardDisplayable(card)));
    }

    const card = reviewDeck.find((deckCard) => !activeCards.has(deckCard));
    reviewDeck = reviewDeck.filter((deckCard) => deckCard !== card);

    return card;
  };

  const spawnCard = () => {
    if (activeCards.size >= maxVisibleCards()) return;

    const card = drawCard();
    const zoneIndex = randomAvailableZone(activeZones);

    if (!card || zoneIndex === undefined) return;

    prepareCard(card, zoneIndex);
    activeCards.add(card);
    activeZones.add(zoneIndex);

    card.classList.remove("is-floating");
    card.offsetHeight;
    card.classList.add("is-floating");

    card.addEventListener(
      "animationend",
      () => {
        card.classList.remove("is-floating");
        activeCards.delete(card);
        activeZones.delete(zoneIndex);

        if (activeCards.size === 0) {
          scheduleNext(shortDelay());
          return;
        }

        scheduleNext(randomDelay());
      },
      { once: true }
    );
  };

  const scheduleNext = (delay = randomDelay()) => {
    if (pendingSpawn) return;

    pendingSpawn = window.setTimeout(() => {
      pendingSpawn = null;

      if (activeCards.size >= maxVisibleCards()) {
        scheduleNext(randomDelay());
        return;
      }

      spawnCard();

      if (activeCards.size > 0 && activeCards.size < maxVisibleCards() && Math.random() > 0.45) {
        scheduleNext(randomDelay());
      }
    }, delay);
  };

  spawnCard();
  scheduleNext(randomBetween([900, 2400]));
}
